import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { getNodesAvailable } from 'lib/api/decisionEngine';
import CustomButton from 'lib/designSystem/Button';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React, { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  EditNode,
  NEW_NODE,
  Node,
  NODE_TYPE_NAMES,
  RevisionObjective,
} from '../../../types';

const useStyles = makeStyles(() =>
  createStyles({
    addContainer: {
      height: '100vh',
      backgroundColor: 'ffffff',
      alignItems: 'center',
      width: '100%',
    },
    container: {
      padding: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  }),
);

export const operatorTypes: { id: string; name: string }[] = [
  {
    id: 'eq',
    name: 'Igual a',
  },
  {
    id: 'gt',
    name: 'Mayor a',
  },
  {
    id: 'lt',
    name: 'Menor a',
  },
];

const FatherIntroDiv = (props: {
  newNode: Node;
  decisionNodesAssignation: Node[];
  operatorTypes: { id: string; name: string }[];
}) => {
  const { newNode, decisionNodesAssignation, operatorTypes } = props;
  const fatherNode = decisionNodesAssignation.find(
    (x) => x.id === newNode.fatherNodeId,
  );
  if (!fatherNode?.type) return null;

  const operationName = operatorTypes.find(
    (x) => x.id === fatherNode.operation,
  );

  const typeName = NODE_TYPE_NAMES[fatherNode.type];

  return (
    <div>
      <Typography variant="body1">
        <b>Tipo de operación:</b> {operationName?.name}
      </Typography>
      <Typography variant="body1">
        <b>Tipo de valor:</b> {typeName}
      </Typography>
    </div>
  );
};

export const explanationText = (
  newNode: Node,
  decisionNodesAssignation: Node[],
) => {
  const fatherNode = decisionNodesAssignation.find(
    (x) => x.id === newNode.fatherNodeId,
  );
  if (!fatherNode) {
    return null;
  }
  switch (fatherNode.operation) {
    case 'eq':
      if (newNode.value === '') {
        return `El nodo será ejecutado si el valor es diferente a todos los otros nodos.`;
      } else {
        return `El nodo será ejecutado si el valor es igual a ${newNode.value}.`;
      }
    case 'gt':
      if (newNode.value === '') {
        return `El nodo será ejecutado si el valor es menor a todos los otros nodos.`;
      } else {
        return `El nodo será ejecutado si el valor es mayor a ${newNode.value} y menor que el siguiente nodo. Si no hay otro nodo mayor, este nodo será ejecutado.`;
      }
    case 'lt':
      if (newNode.value === '') {
        return `El nodo será ejecutado si el valor es mayor a todos los otros nodos.`;
      } else {
        return `El nodo será ejecutado si el valor es menor a ${newNode.value} y mayor que el siguiente nodo. Si no hay otro nodo menor, este nodo será ejecutado.`;
      }
    default:
      return '';
  }
};

export const NodeValueChangeDiv = (props: {
  newNode: Node;
  setNewNode: (node: EditNode) => void;
  decisionNodesAssignation: Node[];
  setCanSave: (canSave: boolean) => void;
  canSave: boolean;
  baseCase?: string | null;
}) => {
  const {
    newNode,
    setNewNode,
    decisionNodesAssignation,
    setCanSave,
    canSave,
    baseCase,
  } = props;
  const [isNodeBaseCase, setIsNodeBaseCase] = useState<string | null>(
    baseCase ?? null,
  );

  const handleChangeNodeType = (e: SelectChangeEvent<string | null>) => {
    const { value } = e.target;
    setIsNodeBaseCase(value);
    if (value === 'baseCase') {
      setNewNode({ ...newNode, value: '' });
    }
  };

  const canBaseCase = () => {
    const fatherNode = decisionNodesAssignation.find(
      (x) => x.id === newNode.fatherNodeId,
    );
    if (!fatherNode) {
      return false;
    }
    const haveBaseCaseAlready = fatherNode.ranges.find((x) => x.value === '');
    if (!haveBaseCaseAlready) {
      return true;
    }
    if (haveBaseCaseAlready.nodeId === newNode.id) {
      return true;
    }
    return false;
  };

  const handleChangeNodeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const nodes = [...decisionNodesAssignation];
    let { value } = e.target;
    value = value.toUpperCase();
    const fatherNode = nodes.find((x) => x.id === newNode.fatherNodeId);
    if (fatherNode) {
      const range = fatherNode.ranges.find((x) => x.value === value);
      if (range) setCanSave(false);
      else setCanSave(true);
    }
    setNewNode({ ...newNode, value });
  };

  return (
    <div>
      <Typography gutterBottom>
        Selecciona si es caso base o un nodo con valor.
      </Typography>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="filter-label">Naturaleza del nodo</InputLabel>
        <Select
          labelId="filter-label"
          id="Filtrar por estado"
          name="originNodeId"
          value={isNodeBaseCase}
          onChange={handleChangeNodeType}
          label="Naturaleza del nodo"
        >
          {canBaseCase() && <MenuItem value={'baseCase'}>Caso base</MenuItem>}
          <MenuItem value={'newValue'}>Nodo con valor</MenuItem>
        </Select>
      </FormControl>
      {isNodeBaseCase !== 'baseCase' && (
        <div style={{ padding: '10px 0px' }}>
          <Typography gutterBottom>
            Escribe el valor con el que se ejecutará este nodo.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Valor"
            value={newNode.value}
            onChange={handleChangeNodeValue}
          />
        </div>
      )}
      {!canSave && (
        <Typography color="error" variant="body1" gutterBottom>
          Ya existe un nodo con el mismo valor, debese cambiarlo.
        </Typography>
      )}
      <Typography variant="body1" gutterBottom>
        <b>Explicación:</b> {explanationText(newNode, decisionNodesAssignation)}
      </Typography>
    </div>
  );
};

type Props = {
  setDecisionNodesAssignation: (nodes: Node[]) => void;
  decisionNodesAssignation: Node[];
  revisionObjective: RevisionObjective;
  fatherNodeIdDefault?: number;
  openAddNode: boolean;
  setOpenAddNode: (open: boolean) => void;
  setCounter: (counter: number) => void;
  counter: number;
};

const NewNodeDialog: React.FC<Props> = ({
  setDecisionNodesAssignation,
  decisionNodesAssignation,
  revisionObjective,
  openAddNode,
  setOpenAddNode,
  setCounter,
  counter,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { data } = getNodesAvailable(dispatch, revisionObjective);
  const decisionNodes = data?.decisionNodes || [];
  const [canSave, setCanSave] = useState(true);
  const [newNode, setNewNode] = useState<Node>(NEW_NODE);

  const addRangeToFatherNode = (nodes: Node[], node: Node) => {
    const fatherNodeIndex = nodes.findIndex((x) => x.id === node.fatherNodeId);
    if (fatherNodeIndex !== -1) {
      const fatherNode = nodes[fatherNodeIndex];
      nodes[fatherNodeIndex] = {
        ...fatherNode,
        ranges: [
          ...fatherNode.ranges,
          {
            nodeId: counter,
            value: node.value,
          },
        ],
      };
    }
    return nodes;
  };

  const addNode = () => {
    if (newNode.originNodeId === null || newNode.fatherNodeId === null) {
      return alert('Debes completar todos los campos');
    }
    const nodes = [...decisionNodesAssignation];
    nodes.push({ ...newNode, id: counter });
    addRangeToFatherNode(nodes, newNode);
    const nextCounter = counter + 1;
    setCounter(nextCounter);
    setDecisionNodesAssignation(nodes);
    setNewNode(NEW_NODE);
    setOpenAddNode(false);
  };

  const handleChangeFatherNodeId = (e: SelectChangeEvent<number>) => {
    const value = Number(e.target.value);
    setNewNode({ ...newNode, fatherNodeId: value });
  };

  const handleChangeOrigin = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    const valueStr = String(value);
    const lastSeparatorIndex = valueStr.lastIndexOf('|');

    if (lastSeparatorIndex === -1) {
      const originNodeId = Number(valueStr);
      if (isNaN(originNodeId)) return;

      const node = decisionNodes.find(
        (x) => x.id === originNodeId && !x.metricId,
      );
      if (!node) return;

      return setNewNode({
        ...newNode,
        originNodeId,
        type: node.type,
        description: node.description,
        name: node.name,
        metricId: node.metricId ?? null,
      });
    }

    const originNodeIdStr = valueStr.substring(0, lastSeparatorIndex);
    const metricIdStr = valueStr.substring(lastSeparatorIndex + 1);
    const originNodeId = Number(originNodeIdStr);

    if (isNaN(originNodeId)) return;

    const node = decisionNodes.find(
      (x) =>
        x.id === originNodeId &&
        String(x.metricId ?? '') === (metricIdStr ?? ''),
    );
    if (!node) return;
    return setNewNode({
      ...newNode,
      originNodeId,
      type: node.type,
      description: node.description,
      name: node.name,
      metricId: node.metricId,
    });
  };

  const availableNodeForSelection = () => {
    if (decisionNodesAssignation.length === 0) {
      return decisionNodes.filter((x) => x.type !== 'final');
    } else {
      return decisionNodes;
    }
  };

  return (
    <div className={classes.container}>
      <Dialog
        maxWidth="sm"
        open={openAddNode}
        onClose={() => setOpenAddNode(false)}
      >
        <DialogTitle>Agregar nodo</DialogTitle>
        <DialogContent style={{ width: '500px' }}>
          <div style={{ display: 'flex', alignItems: 'top' }}>
            <Typography variant="h6" gutterBottom fontWeight={400}>
              Nodo padre
            </Typography>
            <LightTooltip
              title={
                <div style={{ padding: '10px' }}>
                  {' '}
                  <Typography variant="body1">
                    El nodo que estás creando se ejecutará cuando el nodo padre
                    (nodo previo) tenga el valor que designes aquí.
                  </Typography>
                </div>
              }
              placement="top"
            >
              <Info style={{ marginLeft: '6px', fontSize: 18 }} />
            </LightTooltip>
          </div>
          <div style={{ padding: '10px 0px' }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="filter-label">Padre del nodo</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                name="fatherNodeId"
                value={newNode.fatherNodeId ?? undefined}
                onChange={handleChangeFatherNodeId}
                label="Padre del nodo"
              >
                {decisionNodesAssignation.length === 0 && (
                  <MenuItem value={0}>Sin padre (Solo el primer nodo)</MenuItem>
                )}
                {decisionNodesAssignation
                  .filter((x) => x.type !== 'final')
                  .map((x, index) => (
                    <MenuItem key={index} value={x.id}>
                      {x.id} | {x.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
          {String(newNode.fatherNodeId) !== '0' && (
            <div style={{ padding: '10px 0px' }}>
              <FatherIntroDiv
                newNode={newNode}
                decisionNodesAssignation={decisionNodesAssignation}
                operatorTypes={operatorTypes}
              />
              <NodeValueChangeDiv
                newNode={newNode}
                setNewNode={setNewNode}
                decisionNodesAssignation={decisionNodesAssignation}
                setCanSave={setCanSave}
                canSave={canSave}
              />
            </div>
          )}
          {decisionNodesAssignation.length !== 0 && (
            <div>
              <div style={{ display: 'flex' }}>
                <Typography variant="h6" gutterBottom fontWeight={400}>
                  Nuevo nodo
                </Typography>
              </div>
            </div>
          )}

          <div style={{ padding: '10px 0px' }}>
            <Typography variant="body1" gutterBottom>
              Selecciona el nodo que se ejecutará en este paso.
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="filter-label">Nodos disponibles</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                name="originNodeId"
                value={
                  newNode.originNodeId != null
                    ? `${newNode.originNodeId}|${newNode.metricId ?? ''}`
                    : ''
                }
                onChange={handleChangeOrigin}
                label="Nodos disponibles"
              >
                {availableNodeForSelection().map((x, index) => (
                  <MenuItem key={index} value={`${x.id}|${x.metricId ?? ''}`}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {newNode.originNodeId !== null && (
            <div style={{ padding: '20ox' }}>
              <Typography variant="body1" gutterBottom>
                <b>Descripción del nodo</b>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {newNode.description}
              </Typography>
            </div>
          )}
          {['amount', 'percentage', 'days', 'money', 'string'].includes(
            newNode.type ?? '',
          ) && (
            <div>
              <div style={{ padding: '10px 0px' }}>
                <FormControl fullWidth>
                  <InputLabel id="filter-label">{'Operador'}</InputLabel>
                  <Select
                    labelId="filter-label"
                    id="Filtrar por estado"
                    fullWidth
                    value={newNode.operation}
                    onChange={(e) =>
                      setNewNode({ ...newNode, operation: e.target.value })
                    }
                    label={'Operador'}
                  >
                    {operatorTypes.map((x, index) => (
                      <MenuItem key={index} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenAddNode(false)}
            color="secondary"
            variant="contained"
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => addNode()}
            color="primary"
            variant="contained"
            disabled={!canSave}
          >
            Agregar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NewNodeDialog;
